<template>
	<section
		v-editable="blok"
		class="container"
		:aria-label="blok.title"
	>
		<h2 class="typo-h1">
			{{ blok.title }}
		</h2>
		<div v-if="blok.subtitle?.length" class="text-body-tertiary text-base mt-2 mb-8 md:mb-10">
			{{ blok.subtitle }}
		</div>

		<div class="grid grid-cols-3 xs:grid-cols-5 md:grid-cols-3 gap-2 mt-4 sm:mt-8 md:mt-10">
			<template v-if="blok.variant === 'firstLarge'">
				<StoryblokComponent
					:blok="blok.children[0]"
					large
				/>
				<StoryblokComponent
					v-for="item in blok.children.slice(1, blok.children.length)"
					:key="item._uid"
					:blok="item"
				/>
			</template>
			<template v-else>
				<StoryblokComponent
					v-for="item in blok.children"
					:key="item._uid"
					:blok="item"
				/>
			</template>
			<div v-if="horizontalTile" class="max-md:flex-col isolate relative flex col-span-3 shadow-md rounded-xl border border-border-tertiary bg-white md:justify-between md:items-center">
				<h3
					v-if="horizontalTile.title"
					class="max-md:text-center max-md:pt-2 typo-h4 text-heading-secondary break-words xs:break-normal md:py-2 md:px-4"
					v-text="horizontalTile.title"
				/>
				<nav>
					<HorizontalScroll
						v-if="isMounted"
						class="flex justify-center items-center max-md:pl-2 max-md:pr-3.5 px-0 py-2 md:px-4"
						role="tablist"
						fade-edges
					>
						<li
							v-for="author in visibleAuthors"
							:key="author.uuid"
							class="relative hover:z-50 flex flex-col items-center gap-2 group -mr-1.5"
							role="tab"
						>
							<AbsoluteInternalLink
								:to="author.full_slug"
								class="w-12 h-12 md:w-16 md:h-16 rounded-full overflow-hidden group-hover:scale-[1.12] transition-transform"
							>
								<NuxtPicture
									v-if="author.content.image?.filename"
									provider="storyblok"
									width="100"
									height="100"
									:modifiers="storyblokFocusModifiers(author.content.image.focus)"
									:src="author.content.image.filename"
									:alt="author.content.image.alt || ''"
									sizes="md:48px inf:96px"
									:img-attrs="{class:'object-cover w-full h-full'}"
								/>
							</AbsoluteInternalLink>
						</li>
						<li v-if="hiddenAuthors > 0" class="relative z-30">
							<div class="w-12 h-12 md:w-16 md:h-16 flex justify-center items-center rounded-full bg-background-secondary">
								<span class="typo-body-xl" v-text="`+${hiddenAuthors}`" />
							</div>
						</li>
					</HorizontalScroll>
				</nav>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
	import useTailwindBreakpoints from '~/composables/useTailwindBreakpoints';
	import type {CategoryStackStoryblok} from '@/storyblok/types';

	interface Props {
		blok: CategoryStackStoryblok
	}
	const props = defineProps<Props>();

	const isMounted = useMounted();
	const breakpoints = useTailwindBreakpoints();
	const maxVisibleAuthors = computed(() => breakpoints.md.value ? 5 : Infinity);
	const horizontalTile = computed(() => firstBlok(props.blok.horizontalTile));
	const authors = computed(() => resolvedStories(horizontalTile.value?.authors));
	const visibleAuthors = computed(() => authors.value.slice(0, maxVisibleAuthors.value));
	const hiddenAuthors = computed(() => authors.value.length - maxVisibleAuthors.value);
</script>
